import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  ServerStackIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Checkbox, Select, SelectOption, Spinner } from '../ui';
import { cn } from '../../shared/lib/cn';
import {
  ConnectorType,
  type ConnectorConfiguration,
  useListConnectors,
} from '../../generated/connectors/connectors';
import { useCatalogCoverageDetails } from '../../hooks/useCatalogRegistry';
import {
  type WarehouseTableListItem,
  useGetConnectorTables,
  useUpdateWorkspaceTableScope,
} from '../../hooks/useWarehouseRegistry';
import {
  type SemanticContextOverviewResponse,
  type SemanticContextSetupReadinessResponse,
} from '../../hooks/useWorkspaceSemanticContext';
import type {
  StructuredAnalyticsDraft,
  StructuredAssetAllowlistRow,
  TextToSqlReadinessState,
  WorkspaceCatalogCoverageRow,
} from './structuredAnalyticsConfig';
import { WorkspaceSemanticSetupSummaryCard } from './WorkspaceSemanticSetupGuide';

type SemanticContextToast = {
  kind: 'success' | 'error' | 'warning' | 'info';
  message: string;
};

type DataSourceSurface = 'structured' | 'unstructured' | 'connectors';

export interface WorkspaceSemanticOverviewPageProps {
  workspaceId: number;
  workspaceName: string;
  workspaceDescription: string;
  draft: StructuredAnalyticsDraft;
  semanticContext?: SemanticContextOverviewResponse;
  connectorId?: number;
  selectedTableIds: number[];
  canChat?: boolean;
  unstructuredCount?: number;
  unstructuredContent?: React.ReactNode;
  unstructuredDocumentCount?: number;
  unstructuredChunkCount?: number;
  connectorsContent?: React.ReactNode;
  overviewEyebrow?: string;
  showConnectorsSurface?: boolean;
  showStructuredSurface?: boolean;
  showOntologyAffordances?: boolean;
  showContextPackAffordances?: boolean;
  onOpenChat?: () => void;
  onOpenWarehouseConnections: () => void;
  onOpenCatalogConnections: () => void;
  onOpenCatalogApplet?: () => void;
  onOpenWarehouseRegistry: () => void;
  onOpenOntology: () => void;
  onOpenContextPacks: () => void;
  onOpenMcp?: () => void;
  onOpenSetupGuide?: () => void;
  setupReadiness?: SemanticContextSetupReadinessResponse | null;
  isSetupReadinessLoading?: boolean;
  isSetupReadinessError?: boolean;
  onSaveStructuredScope: (tableIds: number[], warehouseConnectorId?: number | null) => Promise<void>;
  onNotify: (toast: SemanticContextToast) => void;
}

const WAREHOUSE_CONNECTOR_TYPES = new Set<string>([
  ConnectorType.BIGQUERY,
  ConnectorType.ERP,
  ConnectorType.MARIADB,
  ConnectorType.POSTGRES,
  ConnectorType.SNOWFLAKE,
]);

const DATA_SOURCE_SURFACES: Array<{
  id: DataSourceSurface;
  label: string;
}> = [
  { id: 'unstructured', label: 'Unstructured' },
  { id: 'structured', label: 'Structured' },
  { id: 'connectors', label: 'Connectors' },
];

const PIPELINE_GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

const TABLE_BROWSER_PAGE_SIZE = 50;

interface OverviewTableRow {
  id: number;
  name?: string | null;
  table_external_id: string;
  datasetName?: string | null;
  projectName?: string | null;
  row_count_snapshot?: number | null;
  last_synced_at?: string | null;
  has_drift: boolean;
  selected?: boolean;
}

function connectorLabel(connector: ConnectorConfiguration): string {
  const provider =
    connector.connector_type === ConnectorType.BIGQUERY
      ? 'BigQuery'
      : connector.connector_type === ConnectorType.ERP
        ? 'ERP'
      : connector.connector_type === ConnectorType.MARIADB
        ? 'MariaDB'
      : connector.connector_type === ConnectorType.POSTGRES
        ? 'PostgreSQL'
        : 'Snowflake';
  return `${provider} - ${connector.connector_name}`;
}

function readinessCopy(
  readiness: TextToSqlReadinessState,
  unstructuredReady: boolean,
  structuredContextSupported: boolean,
): {
  label: string;
  variant: 'success' | 'warning' | 'danger' | 'secondary';
  headline: string;
  detail: string;
} {
  if (!structuredContextSupported) {
    return unstructuredReady
      ? {
          label: 'Retrieval ready',
          variant: 'success',
          headline: 'Knowledge retrieval ready',
          detail: 'This workspace uses retrieval from configured knowledge sources. Structured semantic setup is not part of this workspace type.',
        }
      : {
          label: 'Knowledge needed',
          variant: 'warning',
          headline: 'Add workspace knowledge',
          detail: 'Upload documents or configure the supported knowledge source before chat has workspace-specific evidence.',
        };
  }

  if (unstructuredReady && readiness !== 'approved') {
    return {
      label: readiness === 'blocked' ? 'Retrieval ready - SQL blocked' : 'Retrieval ready',
      variant: readiness === 'blocked' ? 'warning' : 'success',
      headline: 'Retrieval ready; structured context needs setup',
      detail: 'Knowledge stores can answer retrieval questions. Structured Text-to-SQL still needs warehouse, catalog, ontology, and runtime activation.',
    };
  }

  switch (readiness) {
    case 'approved':
      return {
        label: unstructuredReady ? 'SQL + retrieval ready' : 'SQL ready',
        variant: 'success',
        headline: unstructuredReady ? 'Approved for chat and MCP' : 'Structured runtime is active',
        detail: unstructuredReady
          ? 'Runtime context is active for this workspace. Click any stage to manage it.'
          : 'Structured context is active. Add knowledge stores when uploaded documents should feed retrieval.',
      };
    case 'draft_pending':
      return {
        label: 'Draft pending review',
        variant: 'warning',
        headline: 'Draft needs review',
        detail: 'Structured semantics have draft changes pending review. Runtime availability stays tied to the backend-reported active pack.',
      };
    case 'blocked':
      return {
        label: 'Blocked',
        variant: 'danger',
        headline: 'Setup has blocking issues',
        detail: 'Resolve the blocking items below before Text-to-SQL can be activated.',
      };
    case 'configured':
      return {
        label: 'Configured',
        variant: 'warning',
        headline: 'Configured, not activated',
        detail: 'Warehouse assets are scoped. Finish ontology review and compile a Runtime Context Pack.',
      };
    case 'needs_setup':
    default:
      return {
        label: 'Setup needed',
        variant: 'secondary',
        headline: 'Connect structured context',
        detail: 'Select a warehouse, choose assets, and add ontology semantics before activation.',
      };
  }
}

function pipelineStatusVariant(state: 'healthy' | 'warning' | 'draft' | 'missing') {
  if (state === 'healthy') return 'success';
  if (state === 'missing') return 'secondary';
  return 'warning';
}

function statusDotClass(state: 'healthy' | 'warning' | 'draft' | 'missing') {
  if (state === 'healthy') return 'bg-emerald-700';
  if (state === 'missing') return 'bg-gray-500';
  return 'bg-amber-700';
}

function catalogBadgeVariant(state: StructuredAssetAllowlistRow['catalogBadge']) {
  if (state === 'matched') return 'success';
  if (state === 'missing') return 'danger';
  return 'warning';
}

function resolverBadge(row: WorkspaceCatalogCoverageRow) {
  if (row.resolverState === 'resolved') return <Badge variant="success">Described</Badge>;
  if (row.resolverState === 'ambiguous') return <Badge variant="warning">Ambiguous</Badge>;
  if (row.resolverState === 'stale') return <Badge variant="warning">Stale</Badge>;
  return <Badge variant="danger">Missing</Badge>;
}

function formatCatalogPath(row: WorkspaceCatalogCoverageRow) {
  return row.qualifiedName;
}

function percent(value: number, total: number) {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
}

function tableEntityId(row: StructuredAssetAllowlistRow) {
  return `table:${row.id}`;
}

function buildPipeline(
  draft: StructuredAnalyticsDraft,
  catalogRows: WorkspaceCatalogCoverageRow[],
  unstructured: {
    knowledgeBaseCount: number;
    documentCount: number;
    chunkCount: number;
  },
) {
  const selectedCount = draft.selectedAssetCount;
  const enrichedCount = catalogRows.filter((row) => row.resolverState === 'resolved').length;
  const ontologyVersion = draft.ontologyVersionLabel?.replace(' (draft)', '') || 'No draft';
  const entityCount = draft.ontology.entities.length;
  const metricCount = draft.ontology.metrics.length;
  const unstructuredReady = unstructured.documentCount > 0 || unstructured.knowledgeBaseCount > 0;
  const warehouseState =
    draft.warehouseStatus === 'connected'
      ? 'healthy'
      : draft.warehouseStatus === 'stale'
        ? 'warning'
        : 'missing';
  const catalogState =
    draft.catalogIndexStatus === 'healthy'
      ? 'healthy'
      : draft.catalogIndexStatus === 'missing'
        ? 'missing'
        : 'warning';
  const ontologyState = draft.activeRuntimePackVersion
    ? 'healthy'
    : entityCount + metricCount > 0
      ? 'draft'
      : 'missing';
  const packState = draft.activeRuntimePackVersion ? 'healthy' : 'missing';

  return [
    {
      step: '01',
      label: 'Retrieval index',
      value: unstructuredReady
        ? `${unstructured.documentCount.toLocaleString()} docs`
        : 'No documents',
      meta: unstructuredReady
        ? `${unstructured.chunkCount.toLocaleString()} chunks indexed`
        : 'Upload files or configure workspace knowledge',
      status: unstructuredReady ? 'healthy' : 'missing',
      statusLabel: unstructuredReady ? 'Healthy' : 'Missing',
    },
    {
      step: '02',
      label: 'Knowledge sources',
      value:
        unstructured.knowledgeBaseCount > 0
          ? `${unstructured.knowledgeBaseCount} ${unstructured.knowledgeBaseCount === 1 ? 'base' : 'bases'}`
          : unstructured.documentCount > 0
            ? 'Shared index'
            : 'None',
      meta:
        unstructured.knowledgeBaseCount > 0
          ? 'Configured retrieval stores available'
          : unstructured.documentCount > 0
            ? 'Workspace document source'
            : 'Upload or configure workspace knowledge',
      status: unstructuredReady ? 'healthy' : 'missing',
      statusLabel: unstructuredReady ? 'Healthy' : 'Missing',
    },
    {
      step: '03',
      label: 'Warehouse',
      value: draft.warehouseLabel || 'Not connected',
      meta: draft.warehouseStatus === 'connected' ? 'Queryable source linked' : 'Select a structured connector',
      status: warehouseState,
      statusLabel: warehouseState === 'healthy' ? 'Healthy' : warehouseState === 'warning' ? 'Review' : 'Missing',
    },
    {
      step: '04',
      label: 'Catalog',
      value: draft.catalogLabel || 'Eliza catalog',
      meta: `${percent(enrichedCount, Math.max(selectedCount, catalogRows.length))}% described`,
      status: catalogState,
      statusLabel: catalogState === 'healthy' ? 'Healthy' : catalogState === 'missing' ? 'Missing' : 'Partial',
    },
    {
      step: '05',
      label: 'Ontology',
      value: ontologyVersion,
      meta: `${entityCount} entities - ${metricCount} metrics`,
      status: ontologyState,
      statusLabel: ontologyState === 'healthy' ? 'Active' : ontologyState === 'draft' ? 'Draft' : 'Missing',
    },
    {
      step: '06',
      label: 'Context pack',
      value: draft.activeRuntimePackVersion || 'No active pack',
      meta: draft.activeRuntimePackVersion ? 'Available for runtime' : 'Compile after ontology review',
      status: packState,
      statusLabel: packState === 'healthy' ? 'Healthy' : 'Missing',
    },
  ] as const;
}

export function WorkspaceSemanticOverviewPage({
  workspaceId,
  workspaceName,
  workspaceDescription,
  draft,
  semanticContext,
  connectorId,
  selectedTableIds,
  canChat = false,
  unstructuredCount = 0,
  unstructuredContent,
  unstructuredDocumentCount = 0,
  unstructuredChunkCount = 0,
  connectorsContent,
  overviewEyebrow = 'Workspace - Agent Mesh Context',
  showConnectorsSurface = true,
  showStructuredSurface = true,
  showOntologyAffordances = true,
  showContextPackAffordances = true,
  onOpenChat,
  onOpenWarehouseConnections,
  onOpenCatalogConnections,
  onOpenCatalogApplet,
  onOpenWarehouseRegistry,
  onOpenOntology,
  onOpenContextPacks,
  onOpenMcp,
  onOpenSetupGuide,
  setupReadiness,
  isSetupReadinessLoading = false,
  isSetupReadinessError = false,
  onSaveStructuredScope,
  onNotify,
}: WorkspaceSemanticOverviewPageProps) {
  const [activeDataSourceSurface, setActiveDataSourceSurface] = useState<DataSourceSurface>(() =>
    draft.selectedAssetCount > 0 && showStructuredSurface ? 'structured' : 'unstructured',
  );
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(selectedTableIds));
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>(connectorId ? String(connectorId) : '');
  const [isSavingScope, setIsSavingScope] = useState(false);
  const [isBrowsingTables, setIsBrowsingTables] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [debouncedTableSearch, setDebouncedTableSearch] = useState('');
  const [tableOffset, setTableOffset] = useState(0);
  const selectedTableIdsKey = selectedTableIds.join(',');
  const activeConnectorId = selectedConnectorId || (connectorId ? String(connectorId) : '');
  const dataSourceSurfaces = useMemo(
    () =>
      DATA_SOURCE_SURFACES.filter((surface) => {
        if (!showStructuredSurface && surface.id === 'structured') return false;
        if (!showConnectorsSurface && surface.id === 'connectors') return false;
        return true;
      }),
    [showConnectorsSurface, showStructuredSurface],
  );
  const unstructuredReady = unstructuredDocumentCount > 0 || unstructuredCount > 0;
  const readiness = readinessCopy(draft.readiness, unstructuredReady, showStructuredSurface);
  const shouldFetchAvailableTables =
    showStructuredSurface &&
    activeDataSourceSurface === 'structured' &&
    Boolean(activeConnectorId) &&
    isBrowsingTables;
  const {
    data: availableTablesResponse,
    isLoading: isAvailableTablesLoading,
    isFetching: isAvailableTablesFetching,
  } = useGetConnectorTables(activeConnectorId || 0, {
    limit: TABLE_BROWSER_PAGE_SIZE,
    offset: tableOffset,
    search: debouncedTableSearch,
    workspaceId,
    enabled: shouldFetchAvailableTables,
  });
  const { data: realCatalogRows } = useCatalogCoverageDetails(workspaceId, false);
  const catalogRows = Array.isArray(realCatalogRows) ? realCatalogRows : draft.catalogRows;
  const pipeline = buildPipeline(draft, catalogRows, {
    knowledgeBaseCount: unstructuredCount,
    documentCount: unstructuredDocumentCount,
    chunkCount: unstructuredChunkCount,
  });
  const visiblePipeline = pipeline.filter((stage) => {
    if (!showStructuredSurface && (stage.label === 'Warehouse' || stage.label === 'Catalog')) return false;
    if (!showOntologyAffordances && stage.label === 'Ontology') return false;
    if (!showContextPackAffordances && stage.label === 'Context pack') return false;
    return true;
  });
  const pipelineGridColumns = PIPELINE_GRID_COLUMNS[Math.min(Math.max(visiblePipeline.length, 1), 6)];
  const activePack = semanticContext?.readiness.active_pack;

  const updateScope = useUpdateWorkspaceTableScope(workspaceId);
  const { data: connectorsData, isLoading: connectorsLoading } = useListConnectors({ is_enabled: true });
  const configuredConnectorTypeCount = useMemo(
    () => new Set((connectorsData?.connectors || []).map((connector) => connector.connector_type)).size,
    [connectorsData?.connectors],
  );

  const warehouseConnectors = useMemo(
    () =>
      (connectorsData?.connectors || []).filter((connector) =>
        WAREHOUSE_CONNECTOR_TYPES.has(String(connector.connector_type)),
      ),
    [connectorsData?.connectors],
  );

  const allowlistRowByTableId = useMemo(
    () => new Map(draft.allowlistRows.map((row) => [Number(row.id), row])),
    [draft.allowlistRows],
  );
  const scopedTableRows = useMemo<OverviewTableRow[]>(() => {
    return (semanticContext?.structured_scope.scoped_tables || []).map((table) => ({
      id: table.id,
      name: table.name,
      table_external_id: table.table_external_id,
      datasetName: table.dataset,
      projectName: table.project,
      row_count_snapshot: table.row_count_snapshot,
      last_synced_at: table.last_synced_at,
      has_drift: table.has_drift,
      selected: true,
    }));
  }, [semanticContext?.structured_scope.scoped_tables]);
  const availableTableRows = useMemo<OverviewTableRow[]>(() => {
    return (availableTablesResponse?.items || []).map((table: WarehouseTableListItem) => ({
      id: table.id,
      name: table.name,
      table_external_id: table.table_external_id,
      datasetName: table.dataset,
      projectName: table.project,
      row_count_snapshot: table.row_count_snapshot,
      last_synced_at: table.last_synced_at,
      has_drift: table.has_drift,
      selected: table.selected,
    }));
  }, [availableTablesResponse?.items]);
  const visibleTableRows = isBrowsingTables ? availableTableRows : scopedTableRows;

  const resolvedCatalogCount = catalogRows.filter((row) => row.resolverState === 'resolved').length;
  const ambiguousCatalogCount = catalogRows.filter((row) => row.resolverState === 'ambiguous').length;
  const staleCatalogCount = catalogRows.filter((row) => row.resolverState === 'stale').length;
  const missingCatalogCount = catalogRows.filter((row) => row.resolverState === 'missing').length;
  const actionCatalogRows = catalogRows.filter((row) => row.resolverState !== 'resolved');
  const catalogTotal = Math.max(catalogRows.length, draft.selectedAssetCount);
  const catalogCoveragePercent = percent(resolvedCatalogCount, catalogTotal);
  const selectedDomains = Array.from(
    new Set(
      draft.allowlistRows
        .filter((row) => row.selected)
        .map((row) => row.dataset)
        .filter(Boolean),
    ),
  ).slice(0, 5);
  const describedAllowlistCount = draft.allowlistRows.filter((row) => row.catalogBadge === 'matched').length;
  const needsMetadataCount = draft.allowlistRows.filter((row) => row.catalogBadge === 'missing').length;
  const activeOntologyEntities = draft.ontology.entities;
  const activeOntologyMetrics = draft.ontology.metrics;
  const activeOntologyJoins = draft.ontology.joins;
  const isElizaPlatformCatalog = Boolean(onOpenCatalogApplet) || !draft.catalogLabel || draft.catalogLabel.toLowerCase().includes('eliza platform catalog');

  useEffect(() => {
    setSelectedIds(new Set(selectedTableIds));
  }, [selectedTableIds, selectedTableIdsKey]);

  useEffect(() => {
    setSelectedConnectorId(connectorId ? String(connectorId) : '');
  }, [connectorId]);

  useEffect(() => {
    if (!dataSourceSurfaces.some((surface) => surface.id === activeDataSourceSurface)) {
      setActiveDataSourceSurface(dataSourceSurfaces[0]?.id ?? 'unstructured');
    }
  }, [activeDataSourceSurface, dataSourceSurfaces]);

  useEffect(() => {
    if (draft.selectedAssetCount === 0 && (unstructuredDocumentCount > 0 || unstructuredCount > 0)) {
      setActiveDataSourceSurface('unstructured');
    }
  }, [draft.selectedAssetCount, unstructuredCount, unstructuredDocumentCount]);

  useEffect(() => {
    if (selectedConnectorId && connectorId && selectedConnectorId !== String(connectorId)) {
      setSelectedIds(new Set());
    }
  }, [connectorId, selectedConnectorId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedTableSearch(tableSearch);
      setTableOffset(0);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [tableSearch]);

  useEffect(() => {
    setTableOffset(0);
    setTableSearch('');
    setDebouncedTableSearch('');
    setIsBrowsingTables(false);
  }, [activeConnectorId]);

  const toggleTable = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const saveScope = async () => {
    setIsSavingScope(true);
    try {
      const tableIds = Array.from(selectedIds);
      const warehouseConnectorId = selectedConnectorId ? Number(selectedConnectorId) : null;
      if (onSaveStructuredScope) {
        await onSaveStructuredScope(tableIds, warehouseConnectorId);
      } else {
        await updateScope.mutateAsync({ table_ids: tableIds });
      }
      onNotify({ kind: 'success', message: 'Workspace warehouse scope updated' });
    } catch (err) {
      onNotify({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to update workspace warehouse scope',
      });
    } finally {
      setIsSavingScope(false);
    }
  };

  const blockerActionAvailable = (target?: string | null) => {
    if (target === 'warehouse' || target === 'catalog' || target === 'assets') {
      return showStructuredSurface;
    }
    if (target === 'runtime_pack') {
      return showContextPackAffordances;
    }
    if (target === 'ontology') {
      return showOntologyAffordances;
    }
    return true;
  };
  const visibleBlockers = draft.blockers.filter((blocker) =>
    blockerActionAvailable(blocker.actionTarget),
  );

  return (
    <div className="mx-auto w-full max-w-[1240px] pb-16">
      <header className="flex flex-col gap-5 pb-7 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
            {overviewEyebrow}
          </p>
          <h1 className="mt-1 font-title text-[40px] leading-[1.05] text-charcoal dark:text-gray-100">
            {workspaceName}
          </h1>
          <p className="mt-2 max-w-3xl text-base leading-6 text-gray-600 dark:text-gray-300">
            {workspaceDescription}
          </p>
        </div>
        <div className="flex flex-col items-start gap-2 lg:items-end">
          {activePack && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Runtime pack v{activePack.version} active
            </p>
          )}
          {canChat && onOpenChat && (
            <Button type="button" size="sm" onClick={onOpenChat}>
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              Open chat
            </Button>
          )}
        </div>
      </header>

      <section className="overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)] dark:border-dark-border dark:bg-dark-surface">
        <div className="flex flex-col gap-5 px-6 py-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 gap-4">
            <div
              className={cn(
                'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl',
                draft.readiness === 'approved'
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
                  : 'bg-eliza-red/10 text-eliza-red',
              )}
            >
              {draft.readiness === 'approved' ? (
                <CheckCircleIcon className="h-6 w-6" />
              ) : (
                <ArrowPathIcon className="h-6 w-6" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                Workspace readiness - {readiness.label}
              </p>
              <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
                {readiness.headline}
              </h2>
              <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
                {draft.activeRuntimePackVersion
                  ? `Pack ${draft.activeRuntimePackVersion} active across ${draft.selectedAssetCount} structured assets. ${readiness.detail}`
                  : readiness.detail}
              </p>
            </div>
          </div>
          <Badge variant={readiness.variant} className="w-fit px-3 py-1">
            {readiness.label.replace('SQL + retrieval ', '')}
          </Badge>
        </div>

        <div className={cn('grid border-y border-gray-200 dark:border-dark-border', pipelineGridColumns)}>
          {visiblePipeline.map((stage, index) => (
            <button
              key={stage.step}
              type="button"
              onClick={
                stage.label === 'Retrieval index' || stage.label === 'Knowledge sources'
                  ? () => setActiveDataSourceSurface('unstructured')
                  : stage.label === 'Warehouse'
                  ? onOpenWarehouseConnections
                  : stage.label === 'Catalog'
                    ? () => document.getElementById('workspace-catalog-coverage')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    : stage.label === 'Ontology'
                      ? onOpenOntology
                      : stage.label === 'Context pack'
                        ? onOpenContextPacks
                        : undefined
              }
              className={cn(
                'min-h-[132px] border-gray-200 px-4 py-5 text-left transition-colors hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-surface-2',
                index > 0 && 'lg:border-l',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500">{stage.step}</span>
                <Badge variant={pipelineStatusVariant(stage.status)} className="gap-1.5 px-2 py-0.5">
                  <span className={cn('h-1.5 w-1.5 rounded-full', statusDotClass(stage.status))} />
                  {stage.statusLabel}
                </Badge>
              </div>
              <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                {stage.label}
              </p>
              <p className="mt-2 truncate text-base font-semibold text-charcoal dark:text-gray-100">
                {stage.value}
              </p>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                {stage.meta}
              </p>
            </button>
          ))}
        </div>

        {visibleBlockers.length > 0 && (
          <div className="space-y-2 bg-gray-50 px-5 py-4 dark:bg-dark-surface-2">
            {visibleBlockers.slice(0, 3).map((blocker, index) => (
              <div
                key={`${blocker.code || blocker.message}-${index}`}
                className={cn(
                  'flex flex-col gap-3 rounded-xl border px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between',
                  blocker.severity === 'error'
                    ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200'
                    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-200',
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                  {blocker.message}
                </span>
                {blocker.actionLabel && blockerActionAvailable(blocker.actionTarget) && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-current"
                    onClick={
                      blocker.actionTarget === 'warehouse'
                        ? onOpenWarehouseConnections
                        : blocker.actionTarget === 'catalog'
                          ? () => document.getElementById('workspace-catalog-coverage')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          : blocker.actionTarget === 'assets'
                            ? () => document.getElementById('workspace-allowlist')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            : blocker.actionTarget === 'runtime_pack'
                              ? onOpenContextPacks
                              : blocker.actionTarget === 'ontology'
                                ? onOpenOntology
                                : undefined
                    }
                  >
                    {blocker.actionLabel}
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {showStructuredSurface && onOpenSetupGuide && (
        <div className="mt-6">
          <WorkspaceSemanticSetupSummaryCard
            readiness={setupReadiness}
            isLoading={isSetupReadinessLoading}
            isError={isSetupReadinessError}
            onOpenSetupGuide={onOpenSetupGuide}
            onOpenWarehouseConnections={onOpenWarehouseConnections}
            onOpenWarehouseScope={() => document.getElementById('workspace-allowlist')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            onOpenCatalogCoverage={() => document.getElementById('workspace-catalog-coverage')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            onOpenOntology={onOpenOntology}
            onOpenContextPacks={onOpenContextPacks}
            onOpenMcp={onOpenMcp}
          />
        </div>
      )}

      {showOntologyAffordances && (
        <section className="mt-8 flex items-center gap-4 rounded-[16px] border border-gray-200 bg-white px-5 py-4 dark:border-dark-border dark:bg-dark-surface">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-eliza-red/10 text-eliza-red">
            <BookOpenIcon className="h-4 w-4" />
          </div>
          <p className="flex-1 text-sm leading-6 text-gray-700 dark:text-gray-300">
            One workspace ontology spans both sides - structured semantics live in the Structured tab,
            retrieval semantics in the Unstructured tab. Connectors feed either.
          </p>
          <Button type="button" variant="link" size="sm" onClick={onOpenOntology}>
            Open unified ontology
            <ArrowRightIcon className="h-4 w-4" />
          </Button>
        </section>
      )}

      {dataSourceSurfaces.length > 1 && (
        <div className="mt-5 inline-flex rounded-full border border-gray-200 bg-white p-1 shadow-sm dark:border-dark-border dark:bg-dark-surface">
          {dataSourceSurfaces.map((surface) => {
            const active = activeDataSourceSurface === surface.id;
            const count =
              surface.id === 'structured'
                ? draft.selectedAssetCount
                : surface.id === 'unstructured'
                  ? unstructuredCount || unstructuredDocumentCount
                  : configuredConnectorTypeCount;
            return (
              <button
                key={surface.id}
                type="button"
                onClick={() => setActiveDataSourceSurface(surface.id)}
                aria-pressed={active}
                className={cn(
                  'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors',
                  active
                    ? 'bg-charcoal text-white dark:bg-white dark:text-charcoal'
                    : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-dark-surface-2',
                )}
              >
                {surface.id === 'structured' ? (
                  <CircleStackIcon className="h-4 w-4" />
                ) : surface.id === 'connectors' ? (
                  <Squares2X2Icon className="h-4 w-4" />
                ) : (
                  <BookOpenIcon className="h-4 w-4" />
                )}
                {surface.label}
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-xs',
                    active ? 'bg-white/15 text-white dark:bg-charcoal/10 dark:text-charcoal' : 'bg-gray-100 text-gray-500',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {activeDataSourceSurface === 'structured' && (
        <div className="mt-8 space-y-10">
          <section>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  Warehouse & catalog
                </p>
                <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
                  Connection status
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  Warehouses run queries; catalogs enrich them. Both are tenant-level connections, configured under Data Connections.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={onOpenWarehouseRegistry}>
                  <ArrowPathIcon className="h-4 w-4" />
                  Refresh status
                </Button>
                <Button type="button" variant="link" size="sm" onClick={onOpenWarehouseConnections}>
                  Manage connections
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <ConnectionCard
                icon={<ServerStackIcon className="h-5 w-5" />}
                eyebrow="Structured connection"
                title={draft.warehouseLabel || 'No warehouse selected'}
                subtitle={draft.warehouseStatus === 'connected' ? 'Queryable source linked' : 'Select a structured database or ERP connection'}
                status={draft.warehouseStatus === 'connected' ? 'Linked' : draft.warehouseStatus === 'stale' ? 'Review' : 'Missing'}
                statusVariant={draft.warehouseStatus === 'connected' ? 'success' : draft.warehouseStatus === 'stale' ? 'warning' : 'secondary'}
                stats={[
                  { label: 'Selected assets', value: String(draft.selectedAssetCount) },
                  { label: 'Connector state', value: draft.warehouseStatus },
                  { label: 'Scope source', value: connectorId ? `#${connectorId}` : 'none' },
                ]}
                footer="Queries execute on the warehouse. Physical inventory lives here, not at the workspace."
                actionLabel="Open connection"
                onAction={onOpenWarehouseConnections}
              />
              <ConnectionCard
                icon={<BookOpenIcon className="h-5 w-5" />}
                eyebrow="Catalog connection"
                title={draft.catalogLabel === 'Metadata coverage' ? 'Eliza Platform Catalog' : draft.catalogLabel || 'Eliza Platform Catalog'}
                subtitle={draft.catalogCoverageHint}
                status={draft.catalogIndexStatus === 'healthy' ? 'Synced' : draft.catalogIndexStatus === 'missing' ? 'No links' : 'Partial'}
                statusVariant={draft.catalogIndexStatus === 'healthy' ? 'success' : draft.catalogIndexStatus === 'missing' ? 'secondary' : 'warning'}
                stats={[
                  { label: 'Enriched', value: `${resolvedCatalogCount}` },
                  { label: 'Coverage', value: `${catalogCoveragePercent}%` },
                  { label: 'Mode', value: 'Derived' },
                ]}
                footer="Catalog context is enrichment, not a queryable Agent Mesh source. Coverage derives from selected assets."
                actionLabel={isElizaPlatformCatalog ? 'Open Catalog' : 'Open connection'}
                onAction={isElizaPlatformCatalog ? (onOpenCatalogApplet ?? onOpenCatalogConnections) : onOpenCatalogConnections}
              />
            </div>
          </section>

          <section id="workspace-allowlist">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  Allowed assets
                </p>
                <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
                  Warehouse allowlist
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  The exact tables and views Text-to-SQL can query in this workspace. Catalog coverage is derived from this list.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" disabled>
                  <FunnelIcon className="h-4 w-4" />
                  Filter
                </Button>
                <Button
                  type="button"
                  variant="brand"
                  size="sm"
                  onClick={() => setIsBrowsingTables(true)}
                  disabled={!activeConnectorId}
                >
                  <PlusIcon className="h-4 w-4" />
                  Browse & add
                </Button>
              </div>
            </div>

            <div className="relative z-10 mt-4 overflow-visible rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
              <div className="grid gap-5 border-b border-gray-200 px-5 py-4 dark:border-dark-border md:grid-cols-4">
                <SummaryStat label="In allowlist" value={String(draft.selectedAssetCount)} detail="tables - views" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
                    Domains
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {selectedDomains.length > 0 ? (
                      selectedDomains.map((domain) => (
                        <Badge key={domain} variant="secondary">{domain}</Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500 dark:text-gray-400">No domains yet</span>
                    )}
                  </div>
                </div>
                <SummaryStat label="Described" value={String(describedAllowlistCount)} detail={`of ${draft.allowlistRows.length || draft.selectedAssetCount}`} />
                <SummaryStat label="Needs metadata" value={String(needsMetadataCount)} detail="missing descriptions" />
              </div>

              <div className="border-b border-gray-200 px-5 py-3 dark:border-dark-border">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={tableSearch}
                    disabled={!activeConnectorId}
                    onFocus={() => setIsBrowsingTables(true)}
                    onChange={(event) => {
                      setIsBrowsingTables(true);
                      setTableSearch(event.target.value);
                    }}
                    placeholder={
                      activeConnectorId
                        ? `Search ${isBrowsingTables ? availableTablesResponse?.total ?? 'available' : draft.selectedAssetCount} assets by name, dataset, or project...`
                        : 'Select a structured connection to browse available assets'
                    }
                    className="h-11 w-full rounded-full border border-gray-200 bg-white pl-10 pr-4 text-sm text-gray-700 outline-none disabled:cursor-not-allowed disabled:text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-200 dark:disabled:text-gray-400"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                {isBrowsingTables && isAvailableTablesLoading ? (
                  <div className="flex h-48 items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Spinner size="sm" />
                    Fetching available tables...
                  </div>
                ) : visibleTableRows.length > 0 ? (
                  <table className="w-full min-w-[920px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                        <th className="px-5 py-3">In allowlist</th>
                        <th className="px-5 py-3">Asset</th>
                        <th className="px-5 py-3">Domain</th>
                        <th className="px-5 py-3">Metadata</th>
                        <th className="px-5 py-3">Rows - fresh</th>
                        <th className="px-5 py-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                      {visibleTableRows.map((row) => {
                        const selected = selectedIds.has(row.id);
                        const draftRow = allowlistRowByTableId.get(row.id);
                        const catalogBadge = draftRow?.catalogBadge ?? 'missing';
                        return (
                          <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2">
                            <td className="px-5 py-3">
                              <Checkbox
                                aria-label={`Include ${row.name}`}
                                checked={selected}
                                onChange={(event) => toggleTable(row.id, event.target.checked)}
                              />
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <TableCellsIcon className="h-4 w-4 text-gray-500" />
                                <div>
                                  <p className="font-semibold text-charcoal dark:text-gray-100">
                                    {row.name || row.table_external_id}
                                  </p>
                                  <p className="font-mono text-xs text-gray-500">
                                    {[row.projectName, row.datasetName].filter(Boolean).join('.') || 'warehouse'} - table
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              <Badge variant="secondary">{row.datasetName || 'Unassigned'}</Badge>
                            </td>
                            <td className="px-5 py-3">
                              <Badge variant={catalogBadgeVariant(catalogBadge)}>
                                {catalogBadge === 'matched' ? 'Described' : catalogBadge === 'missing' ? 'Needs metadata' : catalogBadge}
                              </Badge>
                            </td>
                            <td className="px-5 py-3 text-gray-600 dark:text-gray-300">
                              <span className="block">{row.row_count_snapshot?.toLocaleString() || '-'}</span>
                              <span className="text-xs text-gray-500">source sync</span>
                            </td>
                            <td className="px-5 py-3 text-right">
                              {row.has_drift ? <Badge variant="warning">Review</Badge> : <Badge variant="success">Healthy</Badge>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : draft.allowlistRows.length > 0 ? (
                  <table className="w-full min-w-[820px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                        <th className="px-5 py-3">Asset</th>
                        <th className="px-5 py-3">Domain</th>
                        <th className="px-5 py-3">Metadata</th>
                        <th className="px-5 py-3">Rows - fresh</th>
                        <th className="px-5 py-3 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                      {draft.allowlistRows.map((row) => (
                        <tr key={row.id}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <TableCellsIcon className="h-4 w-4 text-gray-500" />
                              <div>
                                <p className="font-semibold text-charcoal dark:text-gray-100">{row.objectName}</p>
                                <p className="font-mono text-xs text-gray-500">{row.dataset} - {row.objectType}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3"><Badge variant="secondary">{row.dataset}</Badge></td>
                          <td className="px-5 py-3">
                            <Badge variant={catalogBadgeVariant(row.catalogBadge)}>
                              {row.catalogBadge === 'matched' ? 'Described' : row.catalogBadge === 'missing' ? 'Needs metadata' : row.catalogBadge}
                            </Badge>
                          </td>
                          <td className="px-5 py-3 text-gray-600 dark:text-gray-300">
                            <span className="block">{row.rowEstimate}</span>
                            <span className="text-xs text-gray-500">{row.freshness}</span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Badge variant={row.selected ? 'success' : 'secondary'}>{row.selected ? 'Included' : 'Available'}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-5 py-12 text-center">
                    <CircleStackIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
                    <h3 className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">
                      {isBrowsingTables ? 'No matching warehouse assets' : 'No structured assets selected'}
                    </h3>
                    <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                      {isBrowsingTables
                        ? 'Try a different search term or select another structured connection.'
                        : 'Select a structured connection and scope tables before Text-to-SQL can run.'}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={activeConnectorId ? () => setIsBrowsingTables(true) : onOpenWarehouseConnections}
                    >
                      Structured connections
                    </Button>
                  </div>
                )}
              </div>

              <div className="relative z-20 flex flex-col gap-3 border-t border-gray-200 px-5 py-3 text-sm text-gray-500 dark:border-dark-border dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  {isBrowsingTables
                    ? `Showing ${visibleTableRows.length} of ${availableTablesResponse?.total ?? 0} available assets.`
                    : `Showing ${visibleTableRows.length || draft.allowlistRows.length} selected assets.`}
                  {' '}Removing an active ontology asset will warn before save.
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {isBrowsingTables && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={tableOffset === 0 || isAvailableTablesFetching}
                        onClick={() => setTableOffset((prev) => Math.max(prev - TABLE_BROWSER_PAGE_SIZE, 0))}
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!availableTablesResponse?.has_more || isAvailableTablesFetching}
                        onClick={() => setTableOffset((prev) => prev + TABLE_BROWSER_PAGE_SIZE)}
                      >
                        Next
                      </Button>
                      {isAvailableTablesFetching && <Spinner size="xs" />}
                    </>
                  )}
                  <Select
                    value={selectedConnectorId}
                    onValueChange={setSelectedConnectorId}
                    placeholder={connectorsLoading ? 'Loading structured connections...' : 'Structured connection'}
                    disabled={connectorsLoading || warehouseConnectors.length === 0}
                    className="min-w-[260px]"
                  >
                    {warehouseConnectors.map((connector) => (
                      <SelectOption key={connector.id} value={String(connector.id)}>
                        {connectorLabel(connector)}
                      </SelectOption>
                    ))}
                  </Select>
                  <Badge variant="secondary">{selectedIds.size} selected</Badge>
                  <Button
                    type="button"
                    variant="brand"
                    size="sm"
                    onClick={saveScope}
                    disabled={isSavingScope || !selectedConnectorId}
                  >
                    {isSavingScope && <Spinner size="xs" className="text-current" />}
                    Save scope
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section id="workspace-catalog-coverage">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  Catalog coverage
                </p>
                <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
                  Eliza enrichment on selected assets
                </h2>
                <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  Metadata coverage is enrichment, not a queryable source. Workspace ontology controls runtime business meaning.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={onOpenCatalogConnections}>
                <ArrowPathIcon className="h-4 w-4" />
                Resync coverage
              </Button>
            </div>

            <div className="mt-4 overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
              <div className="px-5 py-5">
                <div className="flex flex-wrap items-end gap-3">
                  <span className="font-title text-[32px] leading-none text-charcoal dark:text-gray-100">{catalogCoveragePercent}%</span>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {resolvedCatalogCount} of {catalogTotal} assets described by warehouse, local, AI, or provider metadata
                  </span>
                </div>
                <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-dark-surface-2">
                  <div className="bg-emerald-500" style={{ width: `${percent(resolvedCatalogCount, catalogTotal)}%` }} />
                  <div className="bg-amber-400" style={{ width: `${percent(ambiguousCatalogCount, catalogTotal)}%` }} />
                  <div className="bg-orange-500" style={{ width: `${percent(staleCatalogCount, catalogTotal)}%` }} />
                  <div className="bg-rose-500" style={{ width: `${percent(missingCatalogCount, catalogTotal)}%` }} />
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <LegendDot color="bg-emerald-500" label={`Described - ${resolvedCatalogCount}`} />
                  <LegendDot color="bg-amber-400" label={`Ambiguous - ${ambiguousCatalogCount}`} />
                  <LegendDot color="bg-orange-500" label={`Stale - ${staleCatalogCount}`} />
                  <LegendDot color="bg-rose-500" label={`Missing - ${missingCatalogCount}`} />
                </div>
              </div>

              {actionCatalogRows.length > 0 ? (
                <div className="divide-y divide-gray-200 border-t border-gray-200 dark:divide-dark-border dark:border-dark-border">
                  {actionCatalogRows.slice(0, 4).map((row) => (
                    <div key={row.id} className="grid gap-3 px-5 py-4 text-sm md:grid-cols-[1.3fr_1.5fr_1fr_auto_auto] md:items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <TableCellsIcon className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold text-charcoal dark:text-gray-100">
                            {formatCatalogPath(row).split('.').pop()}
                          </span>
                          {resolverBadge(row)}
                        </div>
                        <p className="mt-1 font-mono text-xs text-gray-500">{formatCatalogPath(row)}</p>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300">
                        {row.resolverState === 'ambiguous'
                          ? 'Catalog returned multiple candidate matches'
                          : row.resolverState === 'stale'
                            ? 'Catalog description older than warehouse schema'
                          : 'No table description in warehouse, local AI draft, or provider catalog'}
                      </p>
                      <Badge variant="secondary" className="w-fit">{row.ownerHint || row.sourceHint || 'Unassigned'}</Badge>
                      <Button type="button" variant="outline" size="sm" disabled>
                        {row.resolverState === 'missing' ? 'Request entry' : row.resolverState === 'stale' ? 'Refresh' : 'Pick match'}
                      </Button>
                      <Button type="button" variant="link" size="sm" onClick={onOpenCatalogConnections}>
                        View
                        <ArrowRightIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-t border-gray-200 px-5 py-8 text-sm text-gray-600 dark:border-dark-border dark:text-gray-300">
                  Metadata coverage is complete for selected assets.
                </div>
              )}
            </div>
          </section>

          <section>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  Semantic ontology - structured slice
                </p>
                <h2 className="mt-1 font-title text-[28px] leading-tight text-charcoal dark:text-gray-100">
                  Structured semantics
                </h2>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                  The structured face of the workspace ontology: entities, metrics, and governed joins. The same ontology also carries unstructured semantics.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={draft.readiness === 'draft_pending' ? 'warning' : 'secondary'}>
                  {draft.readiness === 'draft_pending' ? 'draft pending' : draft.readiness}
                </Badge>
                <Button type="button" variant="outline" size="sm" onClick={onOpenOntology}>
                  <PencilSquareIcon className="h-4 w-4" />
                  Edit draft
                </Button>
                <Button type="button" variant="link" size="sm" onClick={onOpenOntology}>
                  Open unified ontology
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
              <div className="grid gap-4 border-b border-gray-200 px-5 py-4 dark:border-dark-border md:grid-cols-4">
                <SummaryStat label="Active" value={draft.activeRuntimePackVersion || '-'} detail="runtime pack" />
                <SummaryStat
                  label={draft.activeRuntimePackVersion ? 'Active ontology' : 'Draft'}
                  value={draft.ontologyVersionLabel || '-'}
                  detail="workspace ontology"
                />
                <SummaryStat label="Changes from active" value={`+${activeOntologyEntities.length}`} detail={`${activeOntologyMetrics.length} metrics - ${activeOntologyJoins.length} joins`} />
                <SummaryStat label="This slice covers" value={`${activeOntologyEntities.length} entities`} detail={`${draft.selectedAssetCount} structured assets`} />
              </div>

              <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-5 dark:border-dark-border dark:bg-dark-surface-2">
                <div className="flex gap-6">
                  <button type="button" className="border-b-2 border-eliza-red py-3 text-sm font-semibold text-charcoal dark:text-gray-100">
                    Entities <span className="ml-1 text-gray-400">{activeOntologyEntities.length}</span>
                  </button>
                  <button type="button" className="py-3 text-sm font-semibold text-gray-500">
                    Metrics <span className="ml-1 text-gray-400">{activeOntologyMetrics.length}</span>
                  </button>
                  <button type="button" className="py-3 text-sm font-semibold text-gray-500">
                    Joins <span className="ml-1 text-gray-400">{activeOntologyJoins.length}</span>
                  </button>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={onOpenOntology}>
                  <PlusIcon className="h-4 w-4" />
                  Add entity
                </Button>
              </div>

              {activeOntologyEntities.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-dark-border">
                  {activeOntologyEntities.slice(0, 6).map((entity) => {
                    const mappedRows = draft.allowlistRows.filter((row) => entity.physicalTable === tableEntityId(row));
                    const mapped = mappedRows[0];
                    return (
                      <button
                        key={entity.id}
                        type="button"
                        onClick={onOpenOntology}
                        className="grid w-full gap-3 px-5 py-4 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-surface-2 md:grid-cols-[1.4fr_1fr_auto_auto] md:items-center"
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-300">
                            <CircleStackIcon className="h-4 w-4" />
                          </span>
                          <div>
                            <p className="font-semibold text-charcoal dark:text-gray-100">
                              {entity.name}
                              {mapped && (
                                <code className="ml-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-600 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                                  {mapped.dataset}.{mapped.objectName}
                                </code>
                              )}
                            </p>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {entity.description || 'No description yet.'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(entity.synonyms || []).slice(0, 3).map((synonym) => (
                            <Badge key={synonym} variant="secondary">{synonym}</Badge>
                          ))}
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {mapped ? 'Mapped to warehouse' : 'Needs table map'}
                        </span>
                        <ArrowRightIcon className="h-4 w-4 justify-self-end text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="px-5 py-12 text-center">
                  <BookOpenIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <h3 className="mt-3 text-sm font-semibold text-charcoal dark:text-gray-100">No structured ontology yet</h3>
                  <p className="mx-auto mt-1 max-w-xl text-sm leading-6 text-gray-500 dark:text-gray-400">
                    Add entities or metrics before activating Text-to-SQL for a published workspace.
                  </p>
                  <Button type="button" variant="brand" size="sm" className="mt-4" onClick={onOpenOntology}>
                    Manage ontology
                  </Button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {activeDataSourceSurface === 'unstructured' && (
        unstructuredContent ? (
          <div className="mt-8 space-y-6">
            {unstructuredContent}
          </div>
        ) : (
          <PlaceholderSurface
            icon={<BookOpenIcon className="h-5 w-5" />}
            title="Unstructured retrieval semantics"
            description={
              showOntologyAffordances
                ? 'Knowledge collections and retrieval policy stay in the existing Knowledge Base and ontology surfaces until the backend read model exposes richer retrieval semantics.'
                : 'Knowledge collections and retrieval policy stay in the existing Knowledge Base surface for this workspace type.'
            }
            actionLabel={showOntologyAffordances ? 'Open unified ontology' : undefined}
            onAction={showOntologyAffordances ? onOpenOntology : undefined}
          />
        )
      )}

      {activeDataSourceSurface === 'connectors' && (
        connectorsContent ? (
          <div className="mt-8 space-y-6">
            {connectorsContent}
          </div>
        ) : (
          <PlaceholderSurface
            icon={<LinkIcon className="h-5 w-5" />}
            title="Tenant connectors feed workspace context"
            description="Warehouse and catalog connectors remain managed under Data Connections. This overview links to those workflows without duplicating connector state."
            actionLabel="Manage connections"
            onAction={onOpenWarehouseConnections}
          />
        )
      )}
    </div>
  );
}

function ConnectionCard({
  icon,
  eyebrow,
  title,
  subtitle,
  status,
  statusVariant,
  stats,
  footer,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  subtitle: string;
  status: string;
  statusVariant: 'success' | 'warning' | 'secondary';
  stats: Array<{ label: string; value: string }>;
  footer: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <article className="overflow-hidden rounded-[18px] border border-gray-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:border-dark-border dark:bg-dark-surface">
      <div className="flex min-h-[8.25rem] items-start justify-between gap-4 px-5 py-5">
        <div className="flex min-w-0 gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
            {icon}
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
              {eyebrow}
            </p>
            <h3 className="mt-1 truncate text-base font-semibold text-charcoal dark:text-gray-100">{title}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          </div>
        </div>
        <Badge variant={statusVariant}>{status}</Badge>
      </div>
      <div className="grid grid-cols-3 border-y border-gray-200 dark:border-dark-border">
        {stats.map((stat) => (
          <div key={stat.label} className="px-5 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">{stat.label}</p>
            <p className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 bg-gray-50 px-5 py-4 dark:bg-dark-surface-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-5 text-gray-600 dark:text-gray-300">{footer}</p>
        <Button type="button" variant="link" size="sm" onClick={onAction}>
          {actionLabel}
          <ArrowRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </article>
  );
}

function SummaryStat({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold text-charcoal dark:text-gray-100">
        {value} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{detail}</span>
      </p>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-2 w-2 rounded-sm', color)} />
      {label}
    </span>
  );
}

function PlaceholderSurface({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <section className="mt-8 rounded-[18px] border border-dashed border-gray-300 bg-white px-6 py-12 text-center dark:border-dark-border dark:bg-dark-surface">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
        {icon}
      </div>
      <h3 className="mt-4 font-title text-[24px] leading-tight text-charcoal dark:text-gray-100">{title}</h3>
      <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">{description}</p>
      {actionLabel && onAction && (
        <Button type="button" variant="outline" size="sm" className="mt-5" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </section>
  );
}
